import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import { CALENDLY_LINK } from 'constants/common';
import { EWebinarScheduleType } from 'types/common';
import WebinarBooking from 'components/WebinarBooking';
import s from './s.module.less';

type TabKey = 'benefits' | 'how' | 'solutions' | 'why' | 'faq';

const TABS: { key: TabKey; label: string }[] = [
    { key: 'benefits', label: 'Benefits' },
    { key: 'how', label: 'How it works' },
    { key: 'solutions', label: 'Solutions' },
    { key: 'why', label: 'Why Klarity' },
    { key: 'faq', label: 'FAQs' },
];

const STATS = [
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="8" r="3" />
                <path d="M3 20a6 6 0 0 1 12 0" />
                <path d="M18 8v6M15 11h6" />
            </svg>
        ),
        num: '100+',
        label: 'New patients per month for leading providers',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
            </svg>
        ),
        num: '48 hours',
        label: 'Average time from listed to first booking',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
        ),
        num: '1000+',
        label: 'Telehealth practices supported',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 21s-7-6.3-7-11a7 7 0 0 1 14 0c0 4.7-7 11-7 11z" />
                <circle cx="12" cy="10" r="2.5" />
            </svg>
        ),
        num: '50 states',
        label: 'Coverage across the United States',
    },
];

const FEATURES = [
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4" />
            </svg>
        ),
        title: 'Book more patients',
        desc: 'Get discovered by patients actively searching for your specialty and fill your open slots.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
                <path d="M9 12l2 2 4-4" />
            </svg>
        ),
        title: 'Protect your revenue',
        desc: 'Built-in no-show protection and automated reminders cut down cancellations.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="14" height="10" rx="1.5" />
                <path d="M2 18h14" />
                <rect x="17" y="8" width="5" height="10" rx="1.5" />
            </svg>
        ),
        title: 'Work from any device',
        desc: 'Manage your schedule and patients from any web-connected device, anywhere.',
    },
];

const HOW_STEPS = [
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
            </svg>
        ),
        step: 'Step 1',
        title: 'Apply online (10 mins)',
        desc: 'Submit basic info \u2014 no lengthy applications required; real-time AI and human support available. You can decide your rate and services on your own terms.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16z" />
                <path d="M12 8v4l3 2" />
            </svg>
        ),
        step: 'Step 2',
        title: 'Get approved (24-48 hours)',
        desc: 'Our team reviews credentials and activates your profile within 48 hours.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1v22" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
        ),
        step: 'Step 3',
        title: 'Start earning',
        desc: 'Open your availability and begin accepting patients immediately.',
    },
];

const SOLUTIONS = [
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="3" width="16" height="18" rx="2" />
                <path d="M9 8l1.5 1.5L13 7M8 13h8M8 17h5" />
            </svg>
        ),
        title: 'HelloKlarity listing',
        desc: 'Get discovered by patients actively searching for your specialty.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="11" width="14" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
        ),
        title: 'No-Show protection',
        desc: 'Reduce revenue loss by setting your no-show and late cancellation policies.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1v22" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
        ),
        title: 'Flexible payment options',
        desc: 'Accept insurance, cash-pay, or both \u2014 you control your payment policies.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
            </svg>
        ),
        title: 'Patient messaging',
        desc: 'Chat with your patients in a secure patient portal about visit notes and schedule follow-ups.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
        ),
        title: 'Patient appointment reminders',
        desc: 'Automated SMS and email reminders reduce no-shows by up to 60%.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l3 6.3 6.9 1-5 4.9 1.2 6.9L12 18.8 5.9 21l1.2-6.9-5-4.9 6.9-1z" />
            </svg>
        ),
        title: 'Build your online reputation',
        desc: 'Showcase verified patient reviews and build trust with potential patients.',
    },
];

const COMPARISON_ROWS = [
    {
        label: 'Patient volume',
        klarity: 'Consistent new patient volume \u2014 20+ new patients each month.',
        other: 'Lower patient volume \u2014 typically 4-8 patients per month.',
    },
    {
        label: 'Market saturation',
        klarity: 'Fast-growing market \u2014 less crowded, with quickly increasing patient demand.',
        other: 'Highly competitive & saturated \u2014 crowded listings that are difficult to stand out in.',
    },
    {
        label: 'Growth control',
        klarity: 'Flexible growth control \u2014 easily manage and scale your patient intake.',
        other: 'Limited growth flexibility \u2014 less control over patient acquisition pace.',
    },
    {
        label: 'No-show protection',
        klarity: 'Built-in no-show protection \u2014 secure your revenue with no-show safeguards.',
        other: 'Lack of no-show protection \u2014 no effective tools to manage cancellations.',
    },
];

const FAQS = [
    {
        q: 'How quickly will I start getting patients?',
        a: 'Most providers get their first patient booking within 24 hours of going live. Our platform actively matches patients to providers based on location, specialty, and availability.',
    },
    {
        q: 'Are there any upfront costs to list my practice?',
        a: 'No, listing your practice on Klarity is completely free. You only pay for new patient bookings you receive through the platform.',
    },
    {
        q: 'How does Klarity specifically reduce no-shows?',
        a: 'Klarity allows you to post clear no-show policies and securely collect patient credit card information, significantly reducing cancellations.',
    },
    {
        q: 'What kind of practices is Klarity looking for?',
        a: 'We\u2019re actively looking for providers in psychiatry, psychiatric mental health, therapy, family care, urgent care, dentistry, and other specialties. Providers licensed in all 50 states are welcome to apply.',
    },
    {
        q: 'Do I need to manage billing or insurance through Klarity?',
        a: 'You have full flexibility to manage billing and insurance independently, while Klarity supports both self-pay and insurance-covered patients.',
    },
    {
        q: 'Can I integrate Klarity with my existing workflow?',
        a: 'Yes, Klarity integrates seamlessly with Google, Microsoft, and iCal calendars, making it easy to manage your appointments without changing your workflow.',
    },
    {
        q: 'Does Klarity offer patient communication tools?',
        a: 'Yes, Klarity includes integrated messaging and engagement tools, allowing you to communicate securely and efficiently with your patients.',
    },
];

const MarketingLandingPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<TabKey>('benefits');
    const [showWebinar, setShowWebinar] = useState(false);
    const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
    const sectionRefs = useRef<Record<TabKey, HTMLElement | null>>({
        benefits: null,
        how: null,
        solutions: null,
        why: null,
        faq: null,
    });

    const handleTabClick = (key: TabKey) => {
        setActiveTab(key);
        const el = sectionRefs.current[key];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const toggleFaq = (index: number) => {
        setOpenFaqIndex(openFaqIndex === index ? null : index);
    };

    return (
        <div className={s.wrap}>
            {/* Hero */}
            <div className={s.hero}>
                <div>
                    <p className={s.heroAfford}>Pay per booking &middot; Full budget control</p>
                    <h2 className={s.heroTitle}>
                        Fill your schedule faster &mdash; <span className={s.heroHl}>pay only when patients book</span>
                    </h2>
                    <p className={s.heroSub}>
                        Pro-Listings pushes your profile above standard listings in Klarity Health search. You set the
                        bid, you set the budget cap, and you never pay for impressions or clicks &mdash; only booked
                        appointments.
                    </p>
                    <div className={s.heroActions}>
                        <span className={s.getStartedBtn} onClick={() => navigate(PATH.PPA_ONBOARDING_STEPS, { replace: true })}>
                            Get started
                        </span>
                        <span className={s.webinarBtn} onClick={() => setShowWebinar(true)}>
                            Sign-up for Webinars
                        </span>
                    </div>
                </div>
                <div className={s.heroArt} />
            </div>

            {/* Tabs */}
            <div className={s.tabs}>
                {TABS.map((tab) => (
                    <span
                        key={tab.key}
                        className={`${s.tab} ${activeTab === tab.key ? s.tabActive : ''}`}
                        onClick={() => handleTabClick(tab.key)}
                    >
                        {tab.label}
                    </span>
                ))}
            </div>

            {/* Benefits */}
            <section
                className={s.section}
                ref={(el) => {
                    sectionRefs.current.benefits = el;
                }}
            >
                <h3 className={s.sectionTitle}>Why choose Klarity</h3>

                <div className={s.bnTop}>
                    <div className={s.bnHero}>
                        <div className={s.bnHeroText}>
                            <h3 className={s.bnTitle}>Built to fill your schedule.</h3>
                            <div className={s.bnDevices}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="3" width="20" height="14" rx="2" />
                                    <path d="M8 21h8M12 17v4" />
                                </svg>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="5" y="2" width="14" height="20" rx="2" />
                                    <path d="M12 18h.01" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <div className={s.bnStats}>
                        {STATS.map((stat) => (
                            <div key={stat.num} className={s.bnStat}>
                                <div className={s.bnStatIc}>{stat.icon}</div>
                                <div className={s.bnStatNum}>{stat.num}</div>
                                <div className={s.bnStatLabel}>{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={s.bnBand}>
                    {FEATURES.map((feat) => (
                        <div key={feat.title} className={s.bnFeat}>
                            <div className={s.bnFeatIc}>{feat.icon}</div>
                            <div>
                                <h4>{feat.title}</h4>
                                <p>{feat.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* How it works */}
            <section
                className={s.section}
                ref={(el) => {
                    sectionRefs.current.how = el;
                }}
            >
                <h3 className={s.sectionTitle}>Get started in 3 simple steps</h3>
                <p className={s.sectionSub}>From application to first patient in 3-5 days.</p>
                <div className={s.howitGrid}>
                    {HOW_STEPS.map((item) => (
                        <div key={item.step} className={s.howitCard}>
                            <div className={s.howitIcon}>{item.icon}</div>
                            <div className={s.howitStep}>{item.step}</div>
                            <div className={s.howitTitle}>{item.title}</div>
                            <div className={s.howitDesc}>{item.desc}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Solutions */}
            <section
                className={s.section}
                ref={(el) => {
                    sectionRefs.current.solutions = el;
                }}
            >
                <h3 className={s.sectionTitle}>Klarity provides the following practice solutions</h3>
                <p className={s.sectionSub}>Everything you need to run and grow your practice in one place.</p>
                <div className={s.howitGrid}>
                    {SOLUTIONS.map((item) => (
                        <div key={item.title} className={s.howitCard}>
                            <div className={s.howitIcon}>{item.icon}</div>
                            <div className={s.howitTitle}>{item.title}</div>
                            <div className={s.howitDesc}>{item.desc}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Why Klarity */}
            <section
                className={s.section}
                ref={(el) => {
                    sectionRefs.current.why = el;
                }}
            >
                <h3 className={s.sectionTitle}>What makes Klarity unique</h3>
                <p className={s.sectionSub}>How Klarity compares to oversaturated platforms.</p>
                <div className={s.ownScroll}>
                    <table className={s.ownTable}>
                        <thead>
                            <tr>
                                <th aria-label="Feature" />
                                <th className={s.ownCol}>
                                    Klarity <span className={s.ownTag}>Our product</span>
                                </th>
                                <th>Oversaturated platforms</th>
                            </tr>
                        </thead>
                        <tbody>
                            {COMPARISON_ROWS.map((row) => (
                                <tr key={row.label}>
                                    <th>{row.label}</th>
                                    <td className={s.ownCol}>{row.klarity}</td>
                                    <td className={s.ownComp}>{row.other}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className={s.priceNote}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4M12 8h.01" />
                    </svg>
                    Leading providers on Klarity are already seeing 300+ new patients after joining in less than 3
                    months.
                </p>
            </section>

            {/* FAQs */}
            <section
                className={s.section}
                ref={(el) => {
                    sectionRefs.current.faq = el;
                }}
            >
                <h3 className={s.sectionTitle}>Frequently asked questions</h3>
                <div className={s.faqList}>
                    {FAQS.map((faq, idx) => (
                        <div
                            key={faq.q}
                            className={`${s.faqItem} ${openFaqIndex === idx ? s.faqOpen : ''}`}
                        >
                            <div className={s.faqQ} onClick={() => toggleFaq(idx)}>
                                {faq.q}
                                <svg
                                    className={s.faqChev}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M6 9l6 6 6-6" />
                                </svg>
                            </div>
                            <div className={s.faqA}>{faq.a}</div>
                        </div>
                    ))}
                </div>
            </section>

            <WebinarBooking
                show={showWebinar}
                link={CALENDLY_LINK.KLARITY_ONBOARDING_HELP}
                webinarScheduleType={EWebinarScheduleType.KLARITY_ONBOARDING_HELP}
                onClose={() => setShowWebinar(false)}
                title="Sign-up for Webinars"
            />
        </div>
    );
};

export default MarketingLandingPage;
